import { ChangeDetectionStrategy, Component, inject, Injector, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { retry, takeUntil } from 'rxjs/operators';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TUI_DARK_MODE, TuiDialogService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { AppService, OnlineStatusService, PreferencesService } from '@src/core/services';
import { StorageKeys } from '@src/constants/storage';
import { AuthService, StorageService } from '@src/app/modules/auth';
import { AppConfigService } from '@src/app/modules/app-config';
import { ThemeSwitcherService, ThemeBrightness, ThemeBrightnessEnum } from '@src/app/modules/theme-switcher';
import { UpdateAppDialogComponent, OfflineDialogComponent, ManyTabsDialogComponent } from '@src/app/shared/dialogs';
import { TelegramAuthService } from 'src/app/modules/telegram';
import { environment } from '@src/environments/environment';
import { BaseComponent } from '@src/app/modules/base';
import { APP_CONFIG } from '@src/core';
import { MetrikaService } from '@src/app/modules/metrika';

const APP_NAME = 'UNIONS';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends BaseComponent implements OnInit {
  private readonly onlineStatusService = inject(OnlineStatusService);
  private readonly titleService = inject(Title);
  private readonly appConfigService = inject(AppConfigService);
  private readonly appService = inject(AppService);
  private readonly themeSwitcherService = inject(ThemeSwitcherService);
  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly telegramAuthService = inject(TelegramAuthService);
  private readonly authService = inject(AuthService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly metrikaService = inject(MetrikaService);
  private readonly darkMode = inject(TUI_DARK_MODE);

  private offlineDialogSubscription?: Subscription;
  private readonly confirmUpdateAppDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(UpdateAppDialogComponent, this.injector),
    {
      closeable: false,
      dismissible: false,
      required: true,
      size: 's',
    },
  );
  private readonly offlineDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(OfflineDialogComponent, this.injector),
    {
      closeable: false,
      dismissible: false,
      required: true,
      size: 's',
    },
  );
  private readonly manyTabsDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(ManyTabsDialogComponent, this.injector),
    {
      closeable: false,
      dismissible: false,
      size: 'l',
    },
  );

  readonly testingControlEnabled = !!environment.featureFlags?.testingControlEnabled;

  ngOnInit(): void {
    if (!!APP_CONFIG.yandexMetrikaId) {
      this.metrikaService.init(APP_CONFIG.yandexMetrikaId);
    }

    this.onlineStatusService.isOnline$.subscribe(res => this.updateOnlineStatus(res));

    const storageAction = (e: StorageEvent) => {
      if (e.key === StorageKeys.OpenPages && e.newValue) {
        if (!this.authService.isLogged()) {
          this.manyTabsDialog.pipe(takeUntil(this.destroyed$$)).subscribe();
          return;
        }

        this.telegramAuthService.setActiveTab(false);
        this.storageService.removeItem(StorageKeys.OpenPages);
        window.removeEventListener('storage', storageAction);
      }
    };

    window.addEventListener('storage', storageAction); // storageAction срабатывает во всех вкладках, кроме текущей

    this.telegramAuthService.isLogged$.subscribe(isLogged => {
      if (isLogged) {
        this.storageService.setItem(StorageKeys.OpenPages, Date.now().toString()); // Записываем в Local storage дату, чтобы во всех других вкладках сработал EventListener 'storage'
      }
    });

    this.appConfigService.data$.subscribe(appConfig => {
      let appTitle = APP_NAME;
      if (appConfig) {
        appTitle = appConfig.shortName ?? this.translateService.instant('common.labels.noData');

        if (this.themeSwitcherService.getTheme() === null && appConfig.darkThemeByDefault) {
          this.themeSwitcherService.setTheme(true);
        }
      }

      this.titleService.setTitle(appTitle);
      this.cdr.markForCheck();
    });

    this.preferencesService.settings$.subscribe(settings => {
      if (this.themeSwitcherService.getTheme() === null && settings?.darkThemeByDefault) {
        this.themeSwitcherService.setTheme(true);
      }
    });

    this.appService.isCurrentVersionUnsupported().then(res => {
      if (res === true) this.openConfirmUpdateAppDialog();
    });
  }

  get themeMode(): ThemeBrightness | null {
    return this.darkMode() ? ThemeBrightnessEnum.Dark : null;
  }

  openConfirmUpdateAppDialog(): void {
    this.confirmUpdateAppDialog.pipe(takeUntil(this.destroyed$$), retry()).subscribe();
  }

  private updateOnlineStatus(isOnline: boolean) {
    if (isOnline) {
      this.offlineDialogSubscription?.unsubscribe();
    } else {
      this.offlineDialogSubscription = this.offlineDialog.pipe(takeUntil(this.destroyed$$), retry()).subscribe();
    }

    this.cdr.detectChanges();
  }
}
